import { get, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { ReactComponent as UploadIcon } from "assets/images/iconsV2/upload-cloud-02.svg";
import FilePreview from "components/v2/FilePreview/FilePreview";
import { AttachmentBucket } from "types/Common";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";

import { BucketSelectedFile } from "@RequestFormV2/sections/Provider/hooks/attachmentTypes";
import { useBucketAttachment } from "@RequestFormV2/sections/Provider/hooks/useBucketAttachment";
import FileUploadTrigger from "@RequestFormV2/sections/Provider/components/UploadFreightDocumentsSection/FileUploadTrigger";
import FreightDocumentRow from "@RequestFormV2/sections/Provider/components/UploadFreightDocumentsSection/FreightDocumentRow";

interface BucketAttachmentRowProps {
  bucket: AttachmentBucket;
  onAddFile: (file: BucketSelectedFile, bucketName: string) => void;
  onDeleteFile: (file: { id: number }) => void;
}

export default function BucketAttachmentRow({ bucket, onAddFile, onDeleteFile }: BucketAttachmentRowProps) {
  const intl = useIntl();
  const {
    formState: { errors },
  } = useFormContext();
  const {
    selectedFile,
    uploadingFileName,
    uploadInProgress,
    uploadError,
    uploadErrorMsg,
    isDeleting,
    handleFileSelect,
    handleDelete,
    isVisible,
    label,
    isMandatory,
  } = useBucketAttachment(bucket, { onAddFile, onDeleteFile });

  if (!isVisible) return null;

  const uploadLabel = intl.formatMessage({
    id: "request-form.provider.uploadFreightDocuments.uploadFile",
    defaultMessage: "Upload file",
  });

  const showFilePreview = selectedFile || uploadError || isDeleting || uploadInProgress;
  const showUploadTrigger = !selectedFile && !uploadInProgress && !isDeleting;
  const bucketError = get(errors, `bucket_attachments.${bucket.id}`);
  const bucketAttachments = useWatch({ name: "bucket_attachments" }) as Array<{ bucket_id?: number; bucket_name?: string }> | undefined;

  useReactiveReviewClear(
    `bucket_attachments.${bucket.id}`,
    () =>
      (bucketAttachments ?? []).some(
        (file) => file.id === bucket.id || file.bucket_name === bucket.bucket_name,
      ) || Boolean(selectedFile),
    [bucketAttachments, selectedFile, bucket.id, bucket.bucket_name],
  );

  return (
    <FreightDocumentRow label={label} required={isMandatory}>
      <div className="upload-freight-docs__content">
        {showFilePreview && (
          <div className="upload-freight-docs__file-previews">
            <FilePreview
              fileName={selectedFile?.filename ?? uploadingFileName}
              url={selectedFile?.url}
              isLoading={uploadInProgress || isDeleting}
              isError={uploadError && !uploadInProgress && !isDeleting}
              errorMessage={uploadError && !uploadInProgress && !isDeleting ? uploadErrorMsg : undefined}
              onClose={selectedFile && !uploadInProgress && !isDeleting ? handleDelete : undefined}
            />
          </div>
        )}

        {showUploadTrigger && (
          <FileUploadTrigger label={uploadLabel} iconLeft={<UploadIcon />} onChange={handleFileSelect} />
        )}

        {bucketError?.message && <FieldErrorBadge message={String(bucketError.message)} />}
      </div>
    </FreightDocumentRow>
  );
}
